import { useState } from 'react';
import { toast } from 'react-toastify';
import { loadRazorpay } from '../utils/loadRazorpay';
import { proceedToCheckout } from '../services/subscription';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { setSubscriptionCompleted } from '../store/reducers/subscriptionSlice/subscriptionSlice';
import { clearCheckout } from '../store/reducers/checkoutSlice/checkoutSlice';

export const usePayment = () => {
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const initiatePayment = async ({
		billingDetails,
		plan,
		paymentType,
		billingType,
		paymentMethod,
	}) => {
		try {
			setLoading(true);

			const selectedMethod = paymentMethod;
			const isRecurring = paymentType === 'recurring';
			const activeBilling = plan?.billing?.find((item) => item.period === billingType);

			if (!activeBilling) {
				toast.error('Invalid billing plan');
				return;
			}

			const payload = {
				billing: {
					company_name: billingDetails.name,
					phone: billingDetails.phone,
					address_line1: billingDetails.address,
					city: billingDetails.city,
					state: billingDetails.state,
					pincode: billingDetails.pincode,
					country: billingDetails.country,
					gstin_required: billingDetails.gst,
					gstin: billingDetails.gstin || null,
				},
				plan: {
					razorpay_plan_id: activeBilling?.razorpay_plan_id,
					billing_period: activeBilling?.period,
					amount: activeBilling?.amount_in_rupees,
					is_recurring: isRecurring,
					payment_method: selectedMethod,
				},
			};

			const response = await proceedToCheckout(payload);

			if (!response?.success) {
				toast.error(response?.message || 'Unable to proceed checkout');
				return;
			}

			const razorpayLoaded = await loadRazorpay();

			if (!razorpayLoaded) {
				toast.error('Razorpay SDK failed to load');
				return;
			}

			const subscription = response?.data?.subscription;
			const options = {
				key: subscription?.razorpay_key_id,
				subscription_id: subscription?.subscription_id,
				name: 'Pharmnex',
				description: `${subscription?.plan_name} - ${subscription?.billing_period}`,
				currency: subscription?.currency || 'INR',

				prefill: {
					name: billingDetails?.name,
					contact: billingDetails?.phone,
					email: subscription?.customer?.email || '',
				},

				method: {
					upi: selectedMethod === 'upi',
					card: selectedMethod === 'card',
					netbanking: false,
					wallet: false,
					emi: false,
					paylater: false,
				},

				config: {
					display: {
						blocks: {
							upi: { name: 'Pay Using UPI', instruments: [{ method: 'upi' }] },
							card: { name: 'Pay Using Card', instruments: [{ method: 'card' }] },
						},
						sequence: selectedMethod === 'upi' ? ['block.upi'] : ['block.card'],
						preferences: { show_default_blocks: false },
					},
				},

				...(isRecurring && { recurring: 1 }),

				theme: { color: '#3B58FF' },

				handler: async (paymentResponse) => {
					localStorage.setItem('subscriptionCompleted', JSON.stringify(true));
					dispatch(setSubscriptionCompleted(true));
					dispatch(clearCheckout());
					navigate('/payment-success', { replace: true });
				},

				modal: {
					ondismiss: () => {
						toast.error('Payment cancelled');
					},
				},
			};

			const razorpay = new window.Razorpay(options);

			razorpay.on('payment.failed', (response) => {
				toast.error(response?.error?.description || 'Payment failed');
			});

			razorpay.open();
		} catch (error) {
			console.error(error);
			toast.error(error?.response?.data?.message || 'Payment initialization failed');
		} finally {
			setLoading(false);
		}
	};

	const openSubscriptionCheckout = async ({
		subscription,
		billingDetails,
		paymentMethod = 'card',
	}) => {
		try {
			setLoading(true);

			const razorpayLoaded = await loadRazorpay();

			if (!razorpayLoaded) {
				toast.error('Razorpay SDK failed to load');
				return;
			}

			const options = {
				key: subscription.razorpay_key_id,
				subscription_id: subscription.subscription_id,
				name: 'Pharmnex',
				description: `${subscription.plan_name} - ${subscription.billing_period}`,
				currency: subscription.currency || 'INR',

				prefill: {
					name: billingDetails?.company_name,
					contact: billingDetails?.phone,
					email: subscription.customer?.email,
				},

				method: {
					upi: paymentMethod === 'upi',
					card: paymentMethod === 'card',
				},

				recurring: 1,

				theme: {
					color: '#3B58FF',
				},

				handler: () => {
					localStorage.setItem('subscriptionCompleted', JSON.stringify(true));
					dispatch(setSubscriptionCompleted(true));
					window.location.reload();
				},
			};

			new window.Razorpay(options).open();
		} finally {
			setLoading(false);
		}
	};

	return {
		initiatePayment,
		openSubscriptionCheckout,
		loading,
	};
};
